import { action, computed, observable, runInAction } from 'mobx'
import ReportModel from '@/models/ReportModel.js'
import { formatMweiAsUsd, formatMweiAsUsdCompact } from '@/utils/format.js'
import { toCsv } from '@/utils/csv.js'

export default class ReportsView {
  rootStore = null

  @observable accessor isInitializing = false
  @observable accessor isInitialized = false
  @observable accessor isLoading = false
  @observable accessor purchases = []
  @observable accessor revenue = []
  @observable accessor transactions = []

  constructor(rootStore) {
    this.rootStore = rootStore
  }

  init = async () => {
    if (this.isInitializing) return

    runInAction(() => {
      this.isInitializing = true
    })

    await this.#loadData()

    runInAction(() => {
      this.isInitializing = false
      this.isInitialized = true
    })
  }

  @action
  reset = () => {
    this.isLoading = false
    this.purchases = []
    this.revenue = []
    this.transactions = []

    this.isInitializing = false
    this.isInitialized = false
  }

  get api() {
    return this.rootStore.apiStore
  }

  @computed
  get hasPurchases() {
    return this.purchases.length > 0
  }

  @computed
  get hasRevenue() {
    return this.revenue.length > 0
  }

  @computed
  get hasTransactions() {
    return this.transactions.length > 0
  }

  @computed
  get totalPurchased() {
    // FIXME: BE should send aggregates, we have to load everything to calculate them here
    return this.purchases.reduce((acc, x) => acc + x.incomeNet, 0n)
  }

  @computed
  get totalPurchasedFormatted() {
    return formatMweiAsUsd(this.totalPurchased)
  }

  @computed
  get totalPurchasedFormattedCompact() {
    return formatMweiAsUsdCompact(this.totalPurchased)
  }

  @computed
  get totalRevenue() {
    // FIXME: BE should send aggregates, we have to load everything to calculate them here
    return this.revenue.reduce((acc, x) => acc + x.incomeNet, 0n)
  }

  @computed
  get totalRevenueFormatted() {
    return formatMweiAsUsd(this.totalRevenue)
  }

  @computed
  get totalRevenueFormattedCompact() {
    return formatMweiAsUsdCompact(this.totalRevenue)
  }

  getPurchasesCsv = () =>
    toCsv(
      this.revenue.reduce(
        (acc, x) => {
          acc.push([
            x.createdAt,
            x.transactionType,
            x.title,
            x.postId,
            x.startedAt,
            x.endedAt,
            x.durationFormatted,
            x.amountFormatted,
          ])

          return acc
        },
        [['Date', 'Type', 'Title', 'Post ID', 'Started At', 'Ended At', 'Duration', 'Price']]
      )
    )

  getRevenueCsv = () =>
    toCsv(
      this.revenue.reduce(
        (acc, x) => {
          acc.push([
            x.createdAt,
            x.transactionType,
            x.title,
            x.postId,
            x.startedAt,
            x.endedAt,
            x.durationFormatted,
            x.participantCount,
            x.amountFormatted,
            x.feeFormatted,
            x.incomeNetFormatted,
          ])

          return acc
        },
        [
          [
            'Date',
            'Type',
            'Title',
            'Post ID',
            'Started At',
            'Ended At',
            'Duration',
            'Participants',
            'Price',
            'Fee',
            'Revenue',
          ],
        ]
      )
    )

  getTransactionsCsv = () =>
    toCsv(
      this.transactions.reduce(
        (acc, x) => {
          acc.push([
            x.createdAt,
            x.title,
            x.postId,
            x.transactionHash,
            x.amountFormatted,
            x.isTransactionSender,
          ])

          return acc
        },
        [['Date', 'Title', 'Post ID', 'Tx Hash', 'Amount', 'Sending']]
      )
    )

  #loadData = async () => {
    if (this.isLoading) return

    runInAction(() => {
      this.isLoading = true
    })

    await Promise.all([this.#loadPurchases(), this.#loadRevenue(), this.#loadTransactions()])

    runInAction(() => {
      this.isLoading = false
    })
  }

  #loadPurchases = async () => {
    if (this.hasPurchases) return

    const response = await this.api.getPurchaseHistory()
    if (!response?.data) return

    runInAction(() => {
      this.purchases = response.data.map(x => new ReportModel(this.rootStore, 'purchase', x))
    })
  }

  #loadRevenue = async () => {
    if (this.hasRevenue) return

    const response = await this.api.getRevenueHistory()
    if (!response?.data) return

    runInAction(() => {
      this.revenue = response.data.map(x => new ReportModel(this.rootStore, 'revenue', x))
    })
  }

  #loadTransactions = async () => {
    if (this.hasTransactions) return

    const response = await this.api.getTransactionHistory()
    if (!response?.data) return

    runInAction(() => {
      this.transactions = response.data.map(x => new ReportModel(this.rootStore, 'transaction', x))
    })
  }
}
